/**
 * @name AMA (Advanced Market Analysis)
 * @description This indicator is a comprehensive port of the "JR_BO Filter_PB_3.0" Pine Script indicator.
 * It analyzes price action to identify and flag various types of breakout patterns, climax bars, and follow-through bars.
 * The indicator is highly configurable through its `calcParams`.
 *
 * --- SIGNAL CALCULATION METHODS ---
 *
 * The final signal is determined by a sequence of calculations and filters applied in a specific order.
 * A bar can be re-classified multiple times before the final signal is determined.
 *
 * 1.  **Signal 1 (Bull Breakout) & -1 (Bear Breakout)**
 *     - **Description**: A standard breakout of the previous bar's high or low.
 *     - **Conditions**:
 *       - The bar must NOT be an Outside Bar (OB).
 *       - For a Bull BO, `high` must be > `high[1]` and `low` must be >= `low[1]`.
 *       - For a Bear BO, `low` must be < `low[1]` and `high` must be <= `high[1]`.
 *     - **Note**: This is the initial classification and is subject to being overridden by other signal types or filtered out later.
 *
 * 2.  **Signal 2 (Bull Climax) & -2 (Bear Climax)**
 *     - **Description**: A breakout bar that shows signs of exhaustion.
 *     - **Conditions**:
 *       - It must be a breakout bar.
 *       - The breakout distance on one side is significantly larger than the move on the other side (controlled by `_CXfactor`).
 *       - For a Bull CX, `(high - high[1])` is much larger than `(low - low[1])`.
 *     - **Filters**: This signal is invalidated (reverted to the basic BO signal) if:
 *       - The climax bar is also an Outside Bar.
 *       - The previous bar was an Inside Bar.
 *       - The bar's direction reverses compared to the previous bar.
 *       - The bar fails to close beyond the previous bar's extreme (`high[1]` for Bull, `low[1]` for Bear).
 *
 * 3.  **Signal 3 (Bull Outside Bar) & -3 (Bear Outside Bar)**
 *     - **Description**: An Outside Bar whose direction is determined by the side with the larger breakout.
 *     - **Conditions**:
 *       - The bar must be an Outside Bar (OB).
 *       - Direction is determined by comparing `(high - high[1])` with `(low[1] - low)`.
 *       - The larger distance determines the direction (e.g., if `high - high[1]` is greater, it's a Bull OB).
 *
 * 4.  **Signal 4 (Doji)**
 *     - **Description**: A perfectly balanced Outside Bar.
 *     - **Conditions**:
 *       - It is an Outside Bar.
 *       - The breakout distances `(high - high[1])` and `(low[1] - low)` are equal.
 *       - The bar itself is a perfect doji (`close === open` and IBS is 50).
 *
 * 5.  **Signal 5 (Big Bull BO) & -5 (Big Bear BO)**
 *     - **Description**: A breakout bar with an exceptionally large range.
 *     - **Conditions**:
 *       - The bar must have a valid signal and not be an Outside Bar.
 *       - **Mode 1 (Range Factor)**: `BarRange` >= `AvgRange` * `_BigBORangeFactor`.
 *       - **Mode 2 (Z-Score)**: The Z-Score of the bar's range is > 1.05.
 *
 * 6.  **Follow-Through (FT) Flag**
 *     - **Description**: This is not a signal itself but a flag (`FTflag`) that modifies a bar's signal, indicating it's a follow-through to a prior signal.
 *     - **Conditions**:
 *       - `_PaintFTBar` must be enabled.
 *       - The current bar moves in the same direction as the previous signal bar.
 *     - **Logic**: Can be configured to require a simple breakout (`_FTbarMustBO`) or a close beyond the prior bar's extreme (`_FTbarMustCloseBeyond`).
 *     - **Note**: The final color of the bar may change based on the FT flag.
 *
 * --- FILTERING ORDER ---
 * The script applies filters sequentially. For example, a bar identified as a Climax (Signal 2) might later be filtered out by the Range Filter or the final IBS Filter, causing its signal to become 0.
 *
 * --- CHINESE DOCUMENTATION / 中文文档 ---
 *
 * @name AMA (高级市场分析)
 * @description 该指标是 "JR_BO Filter_PB_3.0" Pine 脚本的完整移植版本。
 * 它通过分析价格行为来识别并标记各种类型的突破形态、竭尽K线和延续K线。
 * 该指标可通过 `calcParams` 进行高度配置。
 *
 * --- 信号计算方法 ---
 *
 * 最终信号是由一系列计算和过滤器按特定顺序应用后确定的。
 * 在确定最终信号之前，一根K线可能会被多次重新分类。
 *
 * 1.  **信号 1 (看涨突破) & -1 (看跌突破)**
 *     - **描述**: 对前一根K线高点或低点的标准突破。
 *     - **条件**:
 *       - 当前K线不能是外包线 (OB)。
 *       - 看涨突破: `high` > `high[1]` 且 `low` >= `low[1]`。
 *       - 看跌突破: `low` < `low[1]` 且 `high` <= `high[1]`。
 *     - **注意**: 这是初始分类，可能会被其他信号类型覆盖或在后续被过滤掉。
 *
 * 2.  **信号 2 (看涨竭尽) & -2 (看跌竭尽)**
 *     - **描述**: 显示出衰竭迹象的突破K线。
 *     - **条件**:
 *       - 必须是一根突破K线。
 *       - 一侧的突破距离明显大于另一侧的移动距离 (由 `_CXfactor` 控制)。
 *       - 看涨竭尽: `(high - high[1])` 远大于 `(low - low[1])`。
 *     - **过滤器**: 如果出现以下情况，该信号将失效 (恢复为基础的突破信号):
 *       - 竭尽K线同时也是一根外包线。
 *       - 前一根K线是内包线。
 *       - K线方向与前一根K线相反。
 *       - K线未能收盘于前一根K线的极端价格之上 (看涨时为 `high[1]`, 看跌时为 `low[1]`)。
 *
 * 3.  **信号 3 (看涨外包线) & -3 (看跌外包线)**
 *     - **描述**: 一根外包线，其方向由突破距离较大的一侧决定。
 *     - **条件**:
 *       - 必须是一根外包线 (OB)。
 *       - 通过比较 `(high - high[1])` 和 `(low[1] - low)` 来确定方向。
 *       - 距离较大的一侧决定了方向 (例如, 如果 `high - high[1]` 更大, 则为看涨外包线)。
 *
 * 4.  **信号 4 (十字星)**
 *     - **描述**: 一根完全平衡的外包线。
 *     - **条件**:
 *       - 是一根外包线。
 *       - 向上和向下的突破距离相等。
 *       - K线本身是一根完美的十字星 (`close === open` 且 IBS 为 50)。
 *
 * 5.  **信号 5 (大实体看涨突破) & -5 (大实体看跌突破)**
 *     - **描述**: 一根具有超大实体范围的突破K线。
 *     - **条件**:
 *       - K线必须有一个有效的信号，并且不能是外包线。
 *       - **模式 1 (范围因子)**: `BarRange` >= `AvgRange` * `_BigBORangeFactor`。
 *       - **模式 2 (Z-Score)**: K线范围的Z-Score > 1.05。
 *
 * 6.  **后续K线 (Follow-Through) 标记**
 *     - **描述**: 这本身不是一个信号，而是一个标记 (`FTflag`)，它会修改K线的信号，表明它是对前一个信号的延续。
 *     - **条件**:
 *       - `_PaintFTBar` 参数必须启用。
 *       - 当前K线的移动方向与前一个信号K线的方向相同。
 *     - **逻辑**: 可配置为要求一次新的突破 (`_FTbarMustBO`) 或要求收盘价超越前一K线的极端价格 (`_FTbarMustCloseBeyond`)。
 *     - **注意**: K线的最终颜色可能会根据FT标记而改变。
 *
 * --- 过滤器顺序 ---
 * 脚本按顺序应用过滤器。例如，一个被识别为竭尽(信号 2)的K线，可能随后被范围过滤器或最终的IBS过滤器过滤掉，导致其信号变为0。
 */

import { registerIndicator } from 'klinecharts';

// Helper function to create a Simple Moving Average calculator
function createSma(period) {
  let sum = 0;
  const history = [];
  return (value) => {
    sum += value;
    history.push(value);
    if (history.length > period) {
      sum -= history.shift();
    }
    return history.length < period ? 0 : sum / history.length;
  };
}

// Helper function to create a Standard Deviation and Z-Score calculator
function createZScoreCalculator(period) {
  const history = [];
  const sma = createSma(period);

  return (value) => {
    history.push(value);
    if (history.length > period) {
      history.shift();
    }

    if (history.length < 2) { // Need at least 2 points for variance
      return { stDev: 0, zScore: 0 };
    }

    const avg = sma(value);
    let varianceSum = 0;
    for (const v of history) {
      varianceSum += (v - avg) * (v - avg);
    }
    const variance = varianceSum / (history.length - 1);
    const stDev = Math.sqrt(variance);
    const zScore = stDev > 0 ? (value - avg) / stDev : 0;

    return { stDev, zScore };
  };
}


// Register AMA indicator with full logic from ama.pine
registerIndicator({
  name: "AMA",
  shortName: "AMA",
  figures: [],
 
  calcParams: {
    // === 信号显示控制 (Signal Display Controls) ===
    showBullishBreakouts: 1,           // 显示看涨突破 (Show Bullish Breakouts) [1=启用, 0=禁用]
    showBearishBreakouts: 1,           // 显示看跌突破 (Show Bearish Breakouts) [1=启用, 0=禁用]
    showBigBreakouts: 1,               // 显示大突破 (Show Big Breakouts) [0=禁用, 1=范围因子模式, 2=Z-Score模式]
    bigBreakoutRangeFactor: 1.8,       // 大突破范围倍数 (Big Breakout Range Factor) [1.0-3.0]
    showOutsideBarBreakouts: 0,        // 显示外包线突破 (Show Outside Bar Breakouts) [1=启用, 0=禁用]
    strictOutsideBarMode: 1,           // 严格外包线模式 (Strict Outside Bar Mode) [1=严格, 0=宽松]
    showClimaxBars: 0,                 // 显示竭尽线 (Show Climax Bars) [1=启用, 0=禁用]
    climaxFactor: 1.8,                 // 竭尽因子 (Climax Factor) [1.0-3.0]
    
    // === IBS 过滤器 (Internal Bar Strength Filters) ===
    bullishSignalIBSThreshold: 69,     // 看涨信号IBS阈值 (Bullish Signal IBS Threshold) [-1=禁用, 0-100]
    bearishSignalIBSThreshold: 29,     // 看跌信号IBS阈值 (Bearish Signal IBS Threshold) [-1=禁用, 0-100]
    bullishFTBarIBSThreshold: -1,      // 看涨跟踪线IBS阈值 (Bullish FT Bar IBS Threshold) [-1=禁用, 0-100]
    bearishFTBarIBSThreshold: -1,      // 看跌跟踪线IBS阈值 (Bearish FT Bar IBS Threshold) [-1=禁用, 0-100]
    exemptOutsideBarsFromIBSFilter: 1, // 外包线免于IBS过滤 (Exempt Outside Bars from IBS Filter) [1=免于, 0=不免于]
    
    // === 范围过滤器 (Range Filter) ===
    enableRangeFilter: 0,              // 启用范围过滤器 (Enable Range Filter) [1=启用, 0=禁用]
    rangeLookbackPeriod: 8,            // 范围回望周期 (Range Lookback Period) [1-50]
    exemptOutsideBarsFromRangeFilter: 0, // 外包线免于范围过滤 (Exempt Outside Bars from Range Filter) [1=免于, 0=不免于]
    
    // === 跟踪线控制 (Follow-Through Controls) ===
    paintFollowThroughBars: 0,         // 绘制跟踪线 (Paint Follow-Through Bars) [1=启用, 0=禁用]
    ftBarMustBreakout: 0,              // 跟踪线必须突破 (FT Bar Must Breakout) [1=必须, 0=不必须]
    ftBarMustCloseBeyond: 0,           // 跟踪线必须收于极值外 (FT Bar Must Close Beyond) [1=必须, 0=不必须]
    ftBarsNotRangeLimited: 1,          // 跟踪线不受范围限制 (FT Bars Not Range Limited) [1=不受限, 0=受限]
    ftAfterOutsideBar: 1,              // 外包线后跟踪 (FT After Outside Bar) [1=允许, 0=不允许]
    ftColorSameAsBreakout: 0,          // 跟踪线颜色同突破 (FT Color Same as Breakout) [1=相同, 0=不同]
    
    // === Z-Score 计算 (Z-Score Calculation) ===
    zScoreLookbackLength: 20,          // Z-Score回望长度 (Z-Score Lookback Length) [10-50]
    
    // === 显示控制 (Display Controls) ===
    showLegend: 0,                     // 显示图例 (Show Legend) [1=显示, 0=隐藏]
    
    // === 内部参数映射 (Internal Parameter Mapping - 请勿修改) ===
    // 以下参数用于内部映射到原始Pine Script参数名，请勿直接修改
    _ShowBLBO: 1,
    _ShowBRBO: 1,
    _ShowBigBO: 1,
    _BigBORangeFactor: 1.8,
    _ShowOutsideBarsBO: 1,
    _StrictOB: 1,
    _ShowCX: 1,
    _CXfactor: 1.8,
    _BLsignalIBS: 69,
    _BRsignalIBS: 29,
    _BLFTbarIBS: -1,
    _BRFTbarIBS: -1,
    _DoNotIBSfilterOB: 1,
    _RangeFilter: 1,
    _RangeLookBack: 8,
    _DoNotRangeLimitOB: 1,
    _PaintFTBar: 0,
    _FTbarMustBO: 1,
    _FTbarMustCloseBeyond: 0,
    _FTbarNotRangeLimited: 1,
    _FTafterOB: 1,
    _FTcolorSameAsBO: 0,
    length: 20,
  },

  legend: {
    '1': { color: 'rgba(0, 128, 255, 0.7)', text: '看涨突破 (Bull BO)' },
    '-1': { color: 'rgba(200, 0, 0, 0.7)', text: '看跌突破 (Bear BO)' },
    '2': { color: 'rgba(0, 200, 255, 0.7)', text: '看涨竭尽 (Bull CX)' },
    '-2': { color: 'rgba(255, 128, 255, 0.7)', text: '看跌竭尽 (Bear CX)' },
    '3': { color: 'rgba(0, 128, 0, 0.7)', text: '看涨外包线 (Bull OB)' },
    '-3': { color: 'rgba(255, 128, 0, 0.7)', text: '看跌外包线 (Bear OB)' },
    '4': { color: 'rgba(255, 0, 255, 0.7)', text: '十字星 (Doji)' },
    '5': { color: 'rgba(0, 230, 230, 0.7)', text: '大看涨突破 (Big Bull BO)' },
    '-5': { color: 'rgba(255, 0, 255, 0.7)', text: '大看跌突破 (Big Bear BO)' },
    '11': { color: '#40c8ff', text: '看涨跟踪 (Bull FT)' },
    '-11': { color: 'rgba(255, 0, 0, 0.7)', text: '看跌跟踪 (Bear FT)' },
  },

  calc: (dataList, indicator) => {
    const params = indicator.calcParams;
    
    // 同步用户友好参数到内部参数 (Sync user-friendly params to internal params)
    params._ShowBLBO = params.showBullishBreakouts;
    params._ShowBRBO = params.showBearishBreakouts;
    params._ShowBigBO = params.showBigBreakouts;
    params._BigBORangeFactor = params.bigBreakoutRangeFactor;
    params._ShowOutsideBarsBO = params.showOutsideBarBreakouts;
    params._StrictOB = params.strictOutsideBarMode;
    params._ShowCX = params.showClimaxBars;
    params._CXfactor = params.climaxFactor;
    params._BLsignalIBS = params.bullishSignalIBSThreshold;
    params._BRsignalIBS = params.bearishSignalIBSThreshold;
    params._BLFTbarIBS = params.bullishFTBarIBSThreshold;
    params._BRFTbarIBS = params.bearishFTBarIBSThreshold;
    params._DoNotIBSfilterOB = params.exemptOutsideBarsFromIBSFilter;
    params._RangeFilter = params.enableRangeFilter;
    params._RangeLookBack = params.rangeLookbackPeriod;
    params._DoNotRangeLimitOB = params.exemptOutsideBarsFromRangeFilter;
    params._PaintFTBar = params.paintFollowThroughBars;
    params._FTbarMustBO = params.ftBarMustBreakout;
    params._FTbarMustCloseBeyond = params.ftBarMustCloseBeyond;
    params._FTbarNotRangeLimited = params.ftBarsNotRangeLimited;
    params._FTafterOB = params.ftAfterOutsideBar;
    params._FTcolorSameAsBO = params.ftColorSameAsBreakout;
    params.length = params.zScoreLookbackLength;

    const smaRange = params._RangeFilter > 0 ? createSma(params._RangeLookBack) : null;
    const zScoreFullCalc = params._ShowBigBO === 2 ? createZScoreCalculator(params.length) : null;
    const zScoreBodyCalc = params._ShowBigBO === 2 ? createZScoreCalculator(params.length) : null;
    
    let prevResult = null;

    return dataList.map((data, index) => {
      const { open, high, low, close } = data;
      const prevData = index > 0 ? dataList[index - 1] : null;
      
      let Signal = 0, OB = 0, IB = 0, IBS = 0, BarDir = 0, BOup = 0, BOdn = 0;
      let HHdist = 0, LLdist = 0, BODir = 0, ExceptionFlag = 0, FTflag = 0;
      let BarRange = high - low;
      let AvgRange = 0;
      let BigBOFlag = 0;

      if (prevData) {
        // ----- [ Section 1: Bar Properties ] -----
        if (smaRange) AvgRange = smaRange(BarRange);
        if (high > prevData.high && low < prevData.low) OB = 1;
        else if (params._StrictOB < 1 && ((high > prevData.high && low === prevData.low) || (high === prevData.high && low < prevData.low))) OB = 1;

        if (high < prevData.high && low > prevData.low) IB = 1;
        else if ((high < prevData.high && low === prevData.low) || (high === prevData.high && low > prevData.low)) IB = 1;

        IBS = BarRange > 0 ? ((close - low) / BarRange) * 100 : 50;

        const prevBarDir = prevResult ? prevResult.BarDir : 0;
        if (close > open && IBS >= 50) BarDir = 1;
        else if (close < open && IBS <= 50) BarDir = -1;
        else if (close === open && IBS > 50) BarDir = 1;
        else if (close === open && IBS < 50) BarDir = -1;
        else if (close === open && IBS === 50) BarDir = prevBarDir;
        else if (close < open && IBS > 50) BarDir = 1;
        else if (close > open && IBS < 50) BarDir = -1;
        else if (IBS === 50) BarDir = prevBarDir;
        else BarDir = prevBarDir;

        // ----- [ Section 2: Initial Breakout Detection ] -----
        BOup = high > prevData.high ? high - prevData.high : 0;
        BOdn = low < prevData.low ? prevData.low - low : 0;

        if (params._ShowBLBO > 0 && OB === 0 && high > prevData.high && low >= prevData.low) Signal = 1;
        if (params._ShowBRBO > 0 && OB === 0 && high <= prevData.high && low < prevData.low) Signal = -1;
        BODir = Signal;

        // ----- [ Section 3: Climax Breakouts ] -----
        if (params._ShowCX > 0) {
          if (BOup > 0 && BOdn === 0) { HHdist = high - prevData.high; LLdist = low - prevData.low; }
          if (BOup === 0 && BOdn > 0) { LLdist = prevData.low - low; HHdist = prevData.high - high; }
          
          if (HHdist > LLdist * params._CXfactor) Signal = 2;
          else if (LLdist > HHdist * params._CXfactor) Signal = -2;

          if (Math.abs(Signal) === 2) {
            const prevIB = prevResult ? prevResult.IB : 0;
            if (prevIB === 1 || OB === 1 || BarDir !== prevBarDir || (Signal === 2 && close <= prevData.high) || (Signal === -2 && close >= prevData.low)) {
              Signal = BODir;
            }
          }
        }

        // ----- [ Section 4: Outside Bar Breakouts ] -----
        if (params._ShowOutsideBarsBO > 0 && OB > 0) {
          HHdist = high - prevData.high;
          LLdist = prevData.low - low;
          if (HHdist > LLdist) Signal = 3;
          else if (HHdist < LLdist) Signal = -3;
          else {
            if (close === open && IBS === 50) Signal = 4;
            else Signal = BarDir === 1 ? 3 : -3;
          }
        }

        // ----- [ Section 5: Follow-Through Logic ] -----
        // Note: Session gap logic (_IgnoreOpenGap) is not implemented in this JS version
        if (params._PaintFTBar > 0 && prevResult && prevResult.Signal !== 0) {
          FTflag = 0; // Reset FT Flag
          const prevSignalSign = Math.sign(prevResult.Signal);
          if (params._FTbarMustBO > 0 && params._FTbarMustCloseBeyond <= 0) {
            if (prevResult.Signal > 0 && BODir === 1) { Signal = 1; FTflag = 1; }
            else if (prevResult.Signal < 0 && BODir === -1) { Signal = -1; FTflag = -1; }
            else if (BarDir === prevSignalSign) {
              if (OB === 1 && params._ShowOutsideBarsBO > 0) { FTflag = 0; }
              else { Signal = 0; FTflag = 0; }
            }
          }
          if (params._FTbarMustCloseBeyond > 0) {
            if (prevResult.Signal > 0 && close > prevData.high) { Signal = 1; FTflag = 1; }
            else if (prevResult.Signal < 0 && close < prevData.low) { Signal = -1; FTflag = -1; }
            else if (BarDir === prevSignalSign) {
              if (OB === 1 && params._ShowOutsideBarsBO > 0) { FTflag = 0; }
              else { Signal = 0; FTflag = 0; }
            }
            else if (prevResult.Signal > 0 && BODir === 1 && close < prevData.high) { Signal = 0; FTflag = 0; }
            else if (prevResult.Signal < 0 && BODir === -1 && close > prevData.low) { Signal = 0; FTflag = 0; }
          }
        }

        // ----- [ Section 6: Range Filter ] -----
        if (params._RangeFilter > 0 && Signal !== 0 && ((params._DoNotRangeLimitOB > 0 && Math.abs(Signal) !== 3) || params._DoNotRangeLimitOB <= 0)) {
          if (params._FTbarNotRangeLimited > 0) {
            ExceptionFlag = 0;
            if (prevResult && prevResult.Signal !== 0 && BarDir === Math.sign(prevResult.Signal)) ExceptionFlag = 1;
            if (params._FTafterOB > 0 && prevResult && Math.abs(prevResult.Signal) === 3 && Math.sign(BarDir) === Math.sign(prevResult.Signal)) ExceptionFlag = 1;
            if (ExceptionFlag === 0 && BarRange < AvgRange) Signal = 0;
          } else if (BarRange < AvgRange) {
            Signal = 0;
          }
        }

        // ----- [ Section 7: Big Breakout Logic ] -----
        if (Signal !== 0 && OB === 0) {
          if (params._ShowBigBO === 1 && AvgRange > 0) {
            if (BarRange >= AvgRange * params._BigBORangeFactor) {
              if (BarDir === 1 && params._ShowBLBO > 0) BigBOFlag = 1;
              else if (BarDir === -1 && params._ShowBRBO > 0) BigBOFlag = -1;
            }
          } else if (params._ShowBigBO === 2 && zScoreFullCalc && zScoreBodyCalc) {
            const bodyRange = Math.abs(close - open);
            const { zScore: zscoreFull } = zScoreFullCalc(BarRange);
            zScoreBodyCalc(bodyRange);
            if (zscoreFull > 1.05) {
              if (BarDir === 1 && params._ShowBLBO > 0) BigBOFlag = 1;
              else if (BarDir === -1 && params._ShowBRBO > 0) BigBOFlag = -1;
            }
          }
          if (BigBOFlag === 1) Signal = 5;
          if (BigBOFlag === -1) Signal = -5;
        }

        // ----- [ Section 8: Final IBS Filter ] -----
        if ((params._DoNotIBSfilterOB > 0 && Math.abs(Signal) !== 3) || params._DoNotIBSfilterOB <= 0) {
          if (params._BLsignalIBS > -1 && Signal > 0 && FTflag === 0 && IBS < params._BLsignalIBS) Signal = 0;
          if (params._BRsignalIBS > -1 && Signal < 0 && FTflag === 0 && IBS > params._BRsignalIBS) Signal = 0;
          if (params._BLFTbarIBS > -1 && Signal > 0 && FTflag === 1 && IBS < params._BLFTbarIBS) { Signal = 0; FTflag = 0; }
          if (params._BRFTbarIBS > -1 && Signal < 0 && FTflag === -1 && IBS > params._BRFTbarIBS) { Signal = 0; FTflag = 0; }
        }
      }
      
      const result = { Signal, BarDir, OB, IB, IBS, BODir, BigBOFlag, FTflag, open, high, low, close };
      prevResult = result;
      return result;
    });
  },

  draw: ({ ctx, chart, indicator, bounding, xAxis, yAxis }) => {
    const { from, to } = chart.getVisibleRange();
    const { gapBar, halfGapBar } = chart.getBarSpace();
    const { result, legend, calcParams } = indicator;

    const ftColorMap = {
      '1': legend['11'].color,
      '-1': legend['-11'].color,
    };
    const boColorMap = {
      '1': legend['1'].color,
      '-1': legend['-1'].color,
    };

    // 绘制K线信号 (Draw candlestick signals)
    for (let i = from; i < to; i++) {
      const data = result[i];
      if (!data) continue;

      const signal = data.Signal;
      const ftFlag = data.FTflag;
      let fillStyle = null;

      if (ftFlag !== 0) {
        if (calcParams._FTcolorSameAsBO <= 0) {
          fillStyle = ftColorMap[ftFlag];
        } else {
          fillStyle = boColorMap[ftFlag];
        }
      } else if (signal !== 0) {
        const legendInfo = legend[signal];
        if (legendInfo) {
          fillStyle = legendInfo.color;
        }
      }

      if (fillStyle) {
        ctx.fillStyle = fillStyle;
        const x = xAxis.convertToPixel(i) - halfGapBar;
        const yOpen = yAxis.convertToPixel(data.open);
        const yClose = yAxis.convertToPixel(data.close);
        
        const yTop = Math.min(yOpen, yClose);
        let bodyHeight = Math.abs(yClose - yOpen);
        if (bodyHeight < 1) {
          bodyHeight = 1;
        }
        ctx.fillRect(x, yTop, gapBar, bodyHeight);
      }
    }

    // 绘制图例 (Draw Legend)
    if (calcParams.showLegend > 0) {
      const legendX = bounding.left + 10;
      const legendY = bounding.top + 10;
      const squareSize = 12;
      const lineHeight = 16;
      const textOffset = 18;
      const padding = 8;
      
      // 计算图例尺寸 (包含标题行)
      const legendWidth = 170;
      const titleHeight = 18; // 标题行高度
      const legendHeight = titleHeight + Object.keys(legend).length * lineHeight + padding * 2 + 2; // +2 for title spacing
      
      // 绘制图例背景 (半透明黑色背景)
      ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
      ctx.fillRect(legendX, legendY, legendWidth, legendHeight);
      
      // 绘制图例边框
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.lineWidth = 1;
      ctx.strokeRect(legendX, legendY, legendWidth, legendHeight);
      
      // 设置文字样式
      ctx.font = '11px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      
      let currentY = legendY + padding + titleHeight/2;
      
      // 绘制标题
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
      ctx.fillText('AMA 信号图例', legendX + padding, currentY);
      currentY += titleHeight + 2;
      
      // 恢复字体
      ctx.font = '10px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
      
      // 绘制每个信号的图例项
      Object.entries(legend).forEach(([signal, info]) => {
        // 绘制颜色方块
        ctx.fillStyle = info.color;
        ctx.fillRect(legendX + padding, currentY - squareSize/2, squareSize, squareSize);
        
        // 绘制方块边框
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.lineWidth = 0.5;
        ctx.strokeRect(legendX + padding, currentY - squareSize/2, squareSize, squareSize);
        
        // 绘制文字
        ctx.fillStyle = '#fff';
        ctx.fillText(info.text, legendX + padding + textOffset, currentY);
        
        currentY += lineHeight;
      });
    }
    
    return false;
  },
});
